"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isOptimizable } from "@/lib/image";

interface Props {
  images: string[];
  alt: string;
  autoPlayInterval?: number;
}

export function ProductImageGallery({
  images,
  alt,
  autoPlayInterval = 4000,
}: Props) {
  const [selected, setSelected] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const dragStartX = useRef<number | null>(null);
  const isDragging = useRef(false);

  const prev = useCallback(() => {
    setSelected((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = useCallback(() => {
    setSelected((i) => (i + 1) % images.length);
  }, [images.length]);

  // 자동 슬라이드
  useEffect(() => {
    if (images.length <= 1 || isPaused) return;
    const timer = setInterval(next, autoPlayInterval);
    return () => clearInterval(timer);
  }, [images.length, isPaused, next, autoPlayInterval]);

  // 마우스 드래그
  function onPointerDown(e: React.PointerEvent) {
    dragStartX.current = e.clientX;
    isDragging.current = false;
    setIsPaused(true);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (dragStartX.current === null) return;
    if (Math.abs(e.clientX - dragStartX.current) > 5) {
      isDragging.current = true;
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    if (dragStartX.current === null) return;
    const delta = e.clientX - dragStartX.current;
    if (isDragging.current) {
      if (delta < -40) next();
      else if (delta > 40) prev();
    }
    dragStartX.current = null;
    isDragging.current = false;
    setIsPaused(false);
  }

  // 터치 스와이프
  const touchStartX = useRef<number | null>(null);

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta < -40) next();
    else if (delta > 40) prev();
    touchStartX.current = null;
    setIsPaused(false);
  }

  const mainSrc = images[selected];

  return (
    <div className="space-y-3">
      {/* 메인 이미지 */}
      <div
        className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] group select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ cursor: images.length > 1 ? "grab" : "default" }}
      >
        <Image
          src={mainSrc}
          alt={alt}
          fill
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="object-cover transition-opacity duration-300 pointer-events-none"
          priority
          unoptimized={!isOptimizable(mainSrc)}
          draggable={false}
        />

        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
                setIsPaused(false);
              }}
              aria-label="이전 이미지"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
                setIsPaused(false);
              }}
              aria-label="다음 이미지"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
            >
              <ChevronRight size={20} />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(i);
                    setIsPaused(false);
                  }}
                  aria-label={`이미지 ${i + 1}`}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{
                    backgroundColor:
                      i === selected ? "white" : "rgba(255,255,255,0.45)",
                    transform: i === selected ? "scale(1.3)" : "scale(1)",
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* 썸네일 */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {images.slice(0, 4).map((img, i) => (
            <button
              key={i}
              onClick={() => {
                setSelected(i);
                setIsPaused(false);
              }}
              className="relative aspect-square rounded-lg overflow-hidden transition-all"
              style={{
                outline:
                  i === selected
                    ? "2px solid var(--color-primary)"
                    : "2px solid transparent",
                opacity: i === selected ? 1 : 0.55,
              }}
            >
              <Image
                src={img}
                alt={`${alt} ${i + 1}`}
                fill
                sizes="25vw"
                className="object-cover"
                unoptimized={!isOptimizable(img)}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
